import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { expandCapture, parseTaskText } from '@nexus/core';
import { addTaskToPage, createMemo, inboxPage, memoFromCapture } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { useProjects } from '../../db/tasks';
import { useCatalog } from '../../state/catalog';
import { useUi } from '../../state/ui';
import { Dialog } from '../Dialog';
import { formatDue, PRIORITY_ICON, todayIso } from './format';

/** Target value for "Onthouden" instead of a page. */
const MEMO = 'memo';

/** Global "add a task" dialog: lands in the Inbox, or in the page you choose (or Onthouden). */
export function QuickCapture() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const { captureOpen, set, tabs, active } = useUi();
  const pages = useCatalog((s) => s.pages);
  const [text, setText] = useState('');
  const [target, setTarget] = useState('');
  const current = tabs[active];

  const expanded = text.trim() ? expandCapture(text.trim(), todayIso()) : '';
  const parsed = expanded ? parseTaskText(expanded) : null;
  const close = () => {
    set({ captureOpen: false });
    setText('');
    setTarget('');
  };
  const save = async () => {
    if (!expanded) return;
    if (target === MEMO) {
      await createMemo(store, memoFromCapture(text, todayIso()));
      close();
      return;
    }
    const pageId = target || (await inboxPage(store)).id;
    await addTaskToPage(store, pageId, expanded);
    close();
  };

  const currentPage =
    current?.kind === 'page' ? pages.find((p) => p.id === current.pageId) : undefined;
  const projects = (useProjects()?.projects ?? [])
    .map((p) => p.page)
    .filter((p) => p.id !== currentPage?.id)
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <Dialog
      open={captureOpen}
      onOpenChange={(o) => (o ? set({ captureOpen: true }) : close())}
      title={t('capture.title')}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('tasks.capturePlaceholder')}
          aria-label={t('tasks.capture')}
          className="w-full rounded-lg border border-border bg-bg px-3 py-2 outline-none focus:border-accent"
        />
        <p className="mt-2 min-h-5 text-xs text-fg-muted" aria-live="polite">
          {parsed && (
            <>
              {parsed.due && (
                <span className="mr-3">
                  {target === MEMO ? '⏰' : '📅'}{' '}
                  {formatDue(parsed.due, i18n.language, t, undefined, parsed.dueTime)}
                </span>
              )}
              {parsed.priority && (
                <span className="mr-3">
                  {PRIORITY_ICON[parsed.priority]} {t(`tasks.priorities.${parsed.priority}`)}
                </span>
              )}
              {parsed.tags.map((g) => (
                <span key={g} className="mr-2">
                  #{g}
                </span>
              ))}
            </>
          )}
          {!parsed && t('capture.help')}
        </p>
        <div className="mt-3 flex items-center gap-2 text-sm">
          <label htmlFor="capture-target" className="text-fg-muted">
            {t('capture.to')}
          </label>
          <select
            id="capture-target"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="min-w-0 flex-1 rounded-lg border border-border bg-bg px-2 py-1"
          >
            <option value="">Inbox</option>
            <option value={MEMO}>📌 {t('memos.title')}</option>
            {currentPage && (
              <option value={currentPage.id}>
                {t('capture.currentPage', { title: currentPage.title || t('page.untitled') })}
              </option>
            )}
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={!expanded}
            className="rounded-full bg-accent px-3 py-1.5 font-medium text-accent-fg disabled:opacity-40"
          >
            {t('capture.add')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
